import type { Strings } from './en';
import { withJosa as j } from './josa';

const SINK_KO = {
  off: '호수 밖으로 밀려납니다',
  hole: '구멍에 빠집니다',
  fall: '턱에서 떨어져 얼음을 깨고 빠집니다',
  crush: '얼음벽에 부딪혀 짓눌립니다',
} as const;

/** Every word the site shows, in Korean. Terms follow the Korean rules sheet. */
export const ko: Strings = {
  langName: '한국어',
  meta: {
    description: '할바르드: 녹아내리는 호수 위의 바이킹 전투.',
  },
  nav: {
    skip: '본문으로 건너뛰기',
    home: '할바르드 홈',
    main: '주 메뉴',
    play: '봇 대전',
    online: '온라인',
    learn: '배우기',
    language: '언어',
  },
  titles: {
    playLocal: '플레이',
    playBot: '봇과 대전',
    botVsBot: '봇 대 봇',
    playFriend: '친구와 대전',
    onlineMatch: '온라인 대전',
    learn: '배우기',
    notFound: '길을 잃었습니다',
  },
  home: {
    tagline: '녹아내리는 호수 위의 바이킹 전투.',
    lead: '적을 얼음 밑으로 밀어내고, 해빙 전에 세 룬스톤을 차지하세요. 호수는 매번 새로 만들어지니 외울 오프닝이 없습니다.',
    playBot: '봇과 대전',
    playOnline: '친구와 온라인 대전',
    playLocal: '한 화면에서 둘이',
    learn: '규칙 배우기',
    picture:
      '얼어붙은 호수에서 진행 중인 게임: 얼음 칸 위의 주황색 브란드 전사들과 청록색 라임 전사들, 뒤로 호숫가와 긴 집.',
    factsTitle: '게임 방식',
    facts: [
      {
        image: '/shove.webp',
        title: '잡지 말고 밀어내기',
        body: '전사를 고르면 닿는 적마다 내 힘 대 그 적의 방패벽이 표시됩니다. 3:2는 성공, 회색은 실패. 흐린 전사는 적이 미끄러질 자리입니다.',
        alt: '확대: 라임 창병 앞에 선 브란드 후스칼, 그 위의 분홍색 3:2 배지, 한 칸 너머의 흐린 창병.',
      },
      {
        image: '/crack.webp',
        title: '얼음이 녹고 있다',
        body: '3라운드마다 두 칸에 금이 갑니다. 붉은 금은 3라운드 뒤 구멍이 되어 그 위의 전사를 삼킵니다.',
        alt: '확대: 붉은 금이 간 얼음 칸.',
      },
      {
        image: '/runestone.webp',
        title: '룬스톤을 지켜라',
        body: '룬스톤은 3×3 얼음판 전체이며, 차지한 쪽의 색으로 테두리가 칠해집니다. 브란드는 16라운드 안에 룬스톤에서 영광 20을 모아야 하고, 라임은 버티기만 하면 됩니다.',
        alt: '확대: 라임이 차지해 청록색 테두리가 칠해진 룬스톤 얼음판.',
      },
    ],
  },
  notFound: {
    title: '길을 잃었습니다',
    body: '없는 페이지입니다.',
    back: '홈으로',
  },

  sides: { brand: '브란드', rime: '라임' },
  warriors: { skier: '스키어', spear: '창병', huscarl: '후스칼' },
  warriorName: (side, type) => `${side} ${type}`,
  warriorRule: {
    skier: '힘 1, 이동력 4. 빠르지만 쉽게 밀립니다.',
    spear: '힘 2, 이동력 3.',
    huscarl: '힘 3, 이동력 2. 느리고 무겁습니다.',
  },
  runestones: ['서쪽 룬스톤', '중앙 룬스톤', '동쪽 룬스톤'],
  bots: {
    easy: { name: '쉬움', detail: '한 수 앞과, 당신의 가장 위험한 밀치기를 봅니다.' },
    normal: { name: '보통', detail: '자기 수와 당신의 응수를 봅니다.' },
    hard: { name: '어려움', detail: '양쪽 모두 두 수 앞을 봅니다.' },
    expert: { name: '전문가', detail: '양쪽 모두 세 수 앞을 봅니다. 느립니다.' },
  },

  game: {
    lakeArea: '호수',
    controls: '게임 조작',
    boardLabel: (seed, status) =>
      `호수 #${seed}. ${status}. 화살표 키로 움직이고 Enter로 선택합니다.`,
    lake: (seed) => `호수 #${seed}`,
    round: (round, of) => `라운드 ${round} / ${of}`,
    hide: '숨기기',
    showControls: '조작',
    players: '플레이어',
    you: '나',
    opponent: '상대',
    bot: (level) => `${level} 봇`,
    sidePlayer: (side) => `${side} 플레이어`,
    toMove: (side) => `${side} 차례`,
    places: (side) => `${side} 전사 배치`,
    botTurn: (side, player, doing) =>
      `${side} (${player}) ${doing === 'thinking' ? '생각 중…' : doing === 'placing' ? '전사 배치' : '차례'}`,
    glory: '영광',
    gloryMeter: '브란드의 영광',
    gloryLine: (glory, target) => `브란드 ${glory} / ${target}`,
    gloryNeeds: (more, turns) => `: ${turns}턴 안에 ${more}점 더 필요`,
    runestonesLabel: '룬스톤',
    holds: (side) => `${side} 장악`,
    nobody: '없음',
    placeHeading: '전사 배치',
    placeGroup: '배치할 전사',
    placeHint: (side, ranks) => `${side} 진영(${ranks}줄)의 빈칸을 누르세요.`,
    shoveTitle: (target) => `${target} 밀치기`,
    shoveFrom: (from) => `${from}에서`,
    pointAnother: '다른 마름모를 가리키면 그 칸에서 밀칩니다. 누르면 둡니다.',
    clickEnemy: '적을 눌러 고른 다음, 어디서 밀칠지 고르세요.',
    shoveFails: '이 밀치기는 실패합니다. 방패벽이 버팁니다. 다른 칸을 고르세요.',
    dotsHelp: '점: 걸어갈 수 있는 칸. 적 위의 숫자: 내 힘 대 그 적의 방패벽.',
    selectWarrior: '내 전사 하나를 고르세요.',
    pass: '넘기기 (움직일 수 없음)',
    undo: '되돌리기',
    cracks: '금',
    groansAt: (round) => `${round}라운드가 끝나면 얼음이 갈라집니다.`,
    noneYet: '아직 없음.',
    crack: (round, opensHole) => [
      `${round}라운드가 끝나면 `,
      opensHole ? '구멍이 됨' : '얇아짐',
      '',
    ],
    log: '기록',
    logEmpty: '브란드가 먼저 배치하고, 이후 번갈아 둡니다.',
    moveLog: '수 기록',
    view: '시점',
    cameraView: '카메라 시점',
    views: { brand: '브란드 쪽', rime: '라임 쪽', top: '위' },
    animations: '애니메이션',
    motion: { auto: '자동', on: '켜기', off: '끄기' },
    reducedNote:
      '시스템이 동작 줄이기를 요청해서 전사가 애니메이션 없이 움직입니다. 걷는 모습을 보려면 켜기를 고르세요.',
    newLake: '새 호수',
    replay: '이 호수 다시 하기',
    gloryResult: (glory, target) => `영광: ${glory} / ${target}.`,
    changeBots: '봇이나 진영 바꾸기',
  },

  board: {
    squareName: (name) => name,
    hole: '구멍',
    ice: (h) => `얼음 ${h}`,
    cracked: (opensHole, round) =>
      `금 감, ${round}라운드가 끝나면 ${opensHole ? '구멍이 됨' : '얇아짐'}`,
    runestone: (name) => name,
    walkHere: '걸어갈 수 있음',
    shoveHere: (strength, wall) => `밀치기 ${strength} 대 ${wall}`,
    tone: {
      works: '',
      'sinks-enemy': ', 적이 빠짐',
      'sinks-ally': ', 내 전사도 빠짐',
      fails: ', 실패',
    },
    shoveFromHere: '여기서 밀치기',
    sinkBadge: {
      off: '호수 밖으로',
      hole: '구멍으로',
      fall: '2단계 낙하',
      crush: '짓눌림',
    },
  },

  log: {
    offTheIce: '호수 밖',
    force: (force, shields) => (shields > 0 ? `힘 ${force} + 방패 ${shields}` : `힘 ${force}`),
    sums: (strength, wall, mine, theirs) => `${strength} 대 ${wall} (${mine} 대 ${theirs})`,
    placed: (warrior, sq) => `${j(warrior, '이/가')} ${sq}에 자리 잡습니다.`,
    moved: (warrior, from, to) => `${j(warrior, '이/가')} ${from}에서 ${j(to, '으로/로')} 갑니다.`,
    shoved: (warrior, target, sq, sums) =>
      `${j(warrior, '이/가')} ${sq}의 ${j(target, '을/를')} 밉니다: ${sums}.`,
    sunk: (warrior, reason) => `${j(warrior, '이/가')} ${SINK_KO[reason]}.`,
    passes: (side) => `${j(side, '은/는')} 움직일 수 없어 차례를 넘깁니다.`,
    glory: (gained, total) => `브란드가 영광 ${gained}점을 얻습니다 (모두 ${total}점).`,
    givesWay: (sq, sunk) =>
      `${sq}의 얼음이 꺼집니다${sunk ? `: ${j(sunk, '이/가')} 빠집니다` : ''}.`,
    thins: (sq, height) => `${sq}의 얼음이 ${j(String(height), '으로/로')} 얇아집니다.`,
    groans: (squares) => `얼음이 갈라집니다: ${squares.join(', ')}에 금이 갑니다.`,
    undone: (notation, next) => `되돌림: ${notation}. ${next}.`,
    lakeStart: (seed) => `호수 #${seed}. 브란드가 먼저 전사를 배치합니다.`,
    lakeNow: (seed, next) => `호수 #${seed}. ${next}.`,
    shoveFailsNow: (strength, wall) =>
      `이 밀치기는 실패합니다: ${strength} 대 ${wall}. 방패벽이 버팁니다.`,
    chooseFrom: '밀칠 칸을 고르세요.',
  },

  outcome: {
    wins: (side) => `${side} 승리`,
    glory: '브란드가 룬스톤에서 충분한 힘을 끌어냈습니다.',
    held: '라임이 해빙까지 호수를 지켜 냈습니다.',
    sunk: (loser) => `${loser}의 전사가 모두 얼음 밑으로 빠졌습니다.`,
    resign: (loser) => `${j(loser, '이/가')} 기권했습니다.`,
  },

  match: {
    you: '당신',
    host: '방장',
    opponent: '상대',
    resigned: (who, defenderQuit, round) =>
      `${j(who, '이/가')} 공격했고, ${defenderQuit ? '수비 측이' : '공격 측이'} ${round}라운드에 기권했습니다`,
    sankAll: (who, round) => `${j(who, '이/가')} ${round}라운드에 수비 전사를 모두 빠뜨렸습니다`,
    reached: (who, glory, round) =>
      `${j(who, '이/가')} ${round}라운드에 영광 ${glory}점을 모았습니다`,
    lostAll: (who, round) => `${j(who, '이/가')} ${round}라운드에 전사를 모두 잃었습니다`,
    heldTo: (who, _isYou, glory) => `${j(who, '은/는')} 영광 ${glory}점에서 막혔습니다`,
    drawn: '무승부입니다',
    youWin: '대전에서 이겼습니다',
    youLose: '대전에서 졌습니다',
    theyWin: (name) => `${j(name, '이/가')} 대전에서 이겼습니다`,
    theWinner: '승자',
    byResign: '한 플레이어가 기권했습니다.',
    byAttackOne: '한쪽 공격만 룬스톤을 차지했습니다.',
    byAttackSingle: '이 게임으로 결정되었습니다.',
    fasterRounds: (fast, slow) =>
      `두 공격 모두 성공해, 더 빠른 쪽이 이겼습니다 (${fast}라운드 대 ${slow}라운드).`,
    fasterSame: '두 공격이 같은 라운드에 성공해, 영광이 더 많은 쪽이 이겼습니다.',
    moreGlory: (most, least) =>
      `두 공격 모두 막혀, 영광이 더 많은 쪽이 이겼습니다 (${most} 대 ${least}).`,
    moreGlorySingle: '영광이 더 많은 쪽이 이겼습니다.',
    level: '두 공격 사이에 차이가 없었습니다.',
  },

  online: {
    title: '친구와 대전',
    intro: '게임을 만들고 친구에게 링크를 보내세요. 링크를 연 사람이 반대편을 맡습니다.',
    format: '방식',
    matchTitle: '대전 (추천)',
    matchDetail: '같은 호수에서 두 게임: 서로 한 번씩 공격합니다.',
    singleTitle: '단판',
    singleDetail: '한 게임: 한 명은 공격, 한 명은 방어합니다.',
    attackFirst: '먼저 공격할까요?',
    yourSide: '내 진영',
    random: '무작위',
    randomDetail: '얼음에 맡깁니다.',
    brandDetail: '공격: 룬스톤을 차지합니다.',
    rimeDetail: '해빙까지 방어합니다.',
    create: '만들고 링크 받기',
    outdatedTitle: '페이지가 오래되었습니다',
    outdatedBody: '게임 서버가 업데이트되었습니다. 계속하려면 페이지를 새로 고치세요.',
    reload: '새로 고침',
    offline: '게임 서버에 연결할 수 없습니다',
    connecting: '연결 중…',
    tryingAgain: '다시 시도하는 중…',
    swap: (first, youAttack) =>
      `${first ? `${first}. ` : ''}이제 진영을 바꿉니다${youAttack === null ? '' : `: 이번엔 당신이 ${youAttack ? '공격' : '방어'}합니다`}.`,
    startGame2: '2게임 시작',
    newMatch: '새 대전',
    waitingTitle: '친구를 기다리는 중',
    waitingDetail: '이 링크를 보내세요. 친구가 열면 게임이 시작됩니다.',
    inviteLink: '초대 링크',
    copied: '복사됨',
    copy: '링크 복사',
    matchGame: (n) => `대전 · 2게임 중 ${n}게임`,
    singleGame: '단판',
    waitingName: '기다리는 중…',
    youSuffix: ' (나)',
    away: '자리 비움',
    youThisGame: (attack, side) =>
      `이번 게임에서 당신은 ${attack ? '공격' : '방어'}합니다 (${side}).`,
    opponentAway: ' 상대가 자리를 비웠습니다.',
    watching: '관전 중입니다.',
    gameN: (n, text) => `${n}게임: ${text}.`,
    reconnecting: '다시 연결하는 중…',
    confirmResign: (match) => `확인: ${match ? '대전 ' : ''}기권`,
    resign: '기권',
    nowWatching: (why) => `${why}. 관전합니다.`,
    errors: {
      GAME_OVER: '게임이 끝났습니다',
      NOT_YOUR_TURN: '당신의 차례가 아닙니다',
      ILLEGAL_ACTION: '둘 수 없는 수입니다',
      GAME_NOT_FOUND: '없는 대전이거나 이미 끝났습니다',
      NOT_A_PLAYER: '이 대전의 플레이어가 아닙니다',
      BAD_JOIN_TOKEN: '올바르지 않은 초대 링크입니다',
      GAME_FULL: '이미 사용된 초대 링크입니다',
      STALE_SEQ: '게임이 진행되어 따라잡는 중입니다',
      RATE_LIMITED: '메시지가 너무 많습니다. 천천히 하세요',
      WAITING_FOR_OPPONENT: '친구가 들어올 때까지 기다리세요',
      CLIENT_OUTDATED: '페이지가 오래되었습니다',
      INTERNAL: '게임 서버에 문제가 생겼습니다',
    },
  },

  botSetup: {
    title: '봇과 대전',
    you: '나',
    brandTitle: '브란드로 플레이',
    brandDetail: '공격: 해빙 전에 룬스톤을 차지합니다.',
    rimeTitle: '라임으로 플레이',
    rimeDetail: '방어: 16라운드까지 호수를 지킵니다.',
    watchTitle: '봇끼리 대결 보기',
    watchDetail: '양쪽 모두 봇이 둡니다.',
    botOf: (side) => `${side} 봇`,
    start: '시작',
  },
};
